import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Package, Star, LogOut, ShoppingCart, Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';

export function AdminLayout() {
  const { signOutUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile/tablet
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path ? 'bg-gray-100 text-indigo-600' : 'text-gray-700 hover:bg-gray-50';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navigationItems = [
    {
      to: "/admin/dashboard",
      icon: <LayoutDashboard className="mr-3 h-5 w-5 flex-shrink-0" />,
      label: "Dashboard"
    },
    {
      to: "/admin/products", 
      icon: <Package className="mr-3 h-5 w-5 flex-shrink-0" />,
      label: "Products"
    },
    {
      to: "/admin/orders",
      icon: <ShoppingCart className="mr-3 h-5 w-5 flex-shrink-0" />,
      label: "Orders"
    },
    {
      to: "/admin/reviews",
      icon: <Star className="mr-3 h-5 w-5 flex-shrink-0" />,
      label: "Reviews"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-full">
        {/* Mobile Header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#D7263D]">Admin Panel</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-40
          ${isMobile && !isMobileMenuOpen ? '-translate-x-full' : 'translate-x-0'}
          ${!isMobile ? 'translate-x-0' : ''}
          w-64 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out
          h-full
        `}>
        {/* Mobile Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          )}
          
          <div className="relative z-40 bg-white h-full">
            {/* Desktop Header */}
            <div className="hidden lg:block p-4 border-b border-gray-200">
              <h1 className="text-xl font-bold text-[#D7263D]">Admin Panel</h1>
            </div>
            
            {/* Mobile Header Space */}
            <div className="lg:hidden h-16"></div>
            
            <nav className="p-4 space-y-1 flex-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors duration-200 ${isActive(item.to)}`}
                >
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                </Link>
              ))}
              
              <Button
                variant="ghost"
                className="w-full justify-start mt-6 px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
                onClick={handleLogout}
              >
                <LogOut className="mr-3 h-5 w-5 flex-shrink-0" />
                <span className="truncate">Logout</span>
              </Button>
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1">
          <main className={`${isMobile ? 'pt-16 pl-0' : 'pt-0 pl-64'} min-h-screen w-full`}>
            <div className="w-full h-full">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
